import { usePersistedQuery } from "@/features/order-sheet-sets/hooks/use-persisted-query";
import { StringTransformer } from "@/features/ui/helpers/string-transformer";
import { SheetTabFilterQuery } from "@/features/order-sheet-sets/sheet-tab-filter";
import { LineSheetSetType } from "@/features/line-sheet-sets/line-sheet-set.types";

export interface OrderSheetSetSelectQuery {
  orderSheetId?: number;
  orderSheetTabIndex?: number;
  revisionNumber?: number;
  lineSheetId?: number;
  lineSheetTabIndex?: number;
  editable?: boolean;
  "nav.type__eq"?: LineSheetSetType;
}

export interface OrderSheetSetPagePersistedQuery
  extends OrderSheetSetSelectQuery,
    SheetTabFilterQuery {
  referer: string;
}

export default function useOrderSheetSetPagePersistedQuery() {
  const { value, set } = usePersistedQuery<OrderSheetSetPagePersistedQuery>({
    entries: {
      referer: {
        parse: StringTransformer.parseString,
        stringify: StringTransformer.stringifyString,
      },
      orderSheetId: {
        parse: StringTransformer.parseId,
        stringify: StringTransformer.stringifyId,
      },
      orderSheetTabIndex: {
        parse: StringTransformer.parseId,
        stringify: StringTransformer.stringifyId,
      },
      revisionNumber: {
        parse: StringTransformer.parseId,
        stringify: StringTransformer.stringifyId,
      },
      lineSheetId: {
        parse: StringTransformer.parseId,
        stringify: StringTransformer.stringifyId,
      },
      brandName__eq: {
        parse: StringTransformer.parseStrings,
        stringify: StringTransformer.stringifyStrings,
      },
      brandId__in: {
        parse: StringTransformer.parseIds,
        // 빈 배열/유효하지 않은 항목이 있으면 undefined를 반환해 URL에서 키가 빠지도록 보장.
        stringify: (rawValue: number[] | undefined): string[] | undefined => {
          if (!rawValue) return undefined;
          const filtered = rawValue.filter(
            (n) => typeof n === "number" && Number.isFinite(n)
          );
          return filtered.length > 0 ? filtered.map((n) => String(n)) : undefined;
        },
      },
      gender__eq: {
        parse: StringTransformer.parseStrings,
        stringify: StringTransformer.stringifyStrings,
      },
      category__eq: {
        parse: StringTransformer.parseStrings,
        stringify: StringTransformer.stringifyStrings,
      },
      season__in: {
        parse: StringTransformer.parseStrings,
        stringify: StringTransformer.stringifyStrings,
      },
      quantity__gte: {
        parse: StringTransformer.parseId,
        stringify: StringTransformer.stringifyId,
      },
      query: {
        parse: StringTransformer.parseString,
        stringify: StringTransformer.stringifyString,
      },
      lineSheetTabIndex: {
        parse: StringTransformer.parseId,
        stringify: StringTransformer.stringifyId,
      },
      "madeByCompany.id__in": {
        parse: StringTransformer.parseIds,
        stringify: StringTransformer.stringifyIds,
      },
      editable: {
        parse: StringTransformer.parseBoolean,
        stringify: StringTransformer.stringifyBoolean,
      },
      "nav.type__eq": {
        parse: StringTransformer.withParseStringLiteral<LineSheetSetType>([
          "SEASON",
          "STOCK",
          "API_ORDER",
        ]),
        stringify: StringTransformer.stringifyStringLiteral,
      },
      orderBy: {
        parse: StringTransformer.parseString,
        stringify: StringTransformer.stringifyString,
      },
    },
  });

  return {
    value,
    set,
  };
}
